import React from 'react';
import { doc, deleteDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import { FullProject, LogEntry, ProjectMetadata } from '../../../types';
import { globalCommandManager, Command } from '../../../utils/commandManager';
import { undoToastManager } from '../../../utils/undoToastManager';
import { areProjectIdsMatching } from '../../../utils/idNormalizer';
import { ProjectSyncTracker } from '../sync/projectSyncStateTracker';
import {
  parsePoolContactDeleteId,
  planPoolContactDeletion,
} from './poolContactDeletePlanner';

export {
  parsePoolContactDeleteId,
  planPoolContactDeletion,
  type ParsedPoolContactId,
  type UnassignedTaskBackup,
  type RemovedStakeholderBackup,
  type PoolContactDeletionPlan,
} from './poolContactDeletePlanner';

export interface ExecutePoolContactDeletionParams {
  taskIdStr: string;
  syncTracker: ProjectSyncTracker;
  setAllProjects: (updater: FullProject[] | ((prev: FullProject[]) => FullProject[])) => void;
  currentProjectId: string;
  setProjectMetadata: React.Dispatch<React.SetStateAction<ProjectMetadata>>;
  addLogEntry: (action: LogEntry['action'], description: string, details?: string) => void;
  markProjectDirty: (id: string) => void;
  updateCurrentProjectTasks: (currentProj: FullProject, updatedProjList: FullProject[]) => void;
  COMM_PROJECT_ID: string;
  onRedoDelete: (taskIdStr: string) => void;
}

export function executePoolContactDeletion({
  taskIdStr,
  syncTracker,
  setAllProjects,
  currentProjectId,
  setProjectMetadata,
  addLogEntry,
  markProjectDirty,
  updateCurrentProjectTasks,
  COMM_PROJECT_ID,
  onRedoDelete,
}: ExecutePoolContactDeletionParams): boolean {
  const parsed = parsePoolContactDeleteId(
    taskIdStr,
    syncTracker.getAllProjects(),
    currentProjectId
  );
  if (!parsed) return false;

  const { poolContactName, poolTargetProjectId } = parsed;
  const plan = planPoolContactDeletion({
    taskIdStr,
    poolContactName,
    poolTargetProjectId,
    currentProjectId,
    COMM_PROJECT_ID,
    allProjects: syncTracker.getAllProjects(),
    rawTasks: syncTracker.getRawTasks(),
    ownedTasks: syncTracker.getOwnedTasks(),
  });

  const {
    targetPId,
    removedTaskIds,
    unassignedTaskIds,
    modifiedProjectIds,
    removedTasksList,
    unassignedTasksToPersist,
    unassignedTasksBackup,
    removedStakeholdersBackup,
    updatedProjects,
    cleanedRawTasks,
    cleanedOwnedTasks,
  } = plan;

  syncTracker.setRawAndOwnedTasks(cleanedRawTasks, cleanedOwnedTasks);
  syncTracker.recordRecentlyDeleted([taskIdStr, ...removedTaskIds]);
  syncTracker.recordDirtyTasks(unassignedTaskIds);
  syncTracker.commitProjects(updatedProjects, setAllProjects);

  const currentInNew = updatedProjects.find((p) =>
    areProjectIdsMatching(p.id, currentProjectId)
  );
  if (currentInNew) {
    updateCurrentProjectTasks(currentInNew, updatedProjects);
    setProjectMetadata(currentInNew.metadata || {});
  }

  removedTaskIds.forEach((remId) => {
    if (remId && !remId.includes('pool_contact_')) {
      const taskDocRef = doc(db, 'tasks', remId);
      deleteDoc(taskDocRef).catch((err) => {
        console.error(
          `[ProjectManager] Failed to delete contact task ${remId} from Firestore:`,
          err
        );
      });
    }
  });

  unassignedTasksToPersist.forEach(({ id: uId, updates }) => {
    if (uId && !uId.includes('pool_contact_')) {
      const taskDocRef = doc(db, 'tasks', uId);
      updateDoc(taskDocRef, updates).catch(() => {});
    }
  });

  if (modifiedProjectIds.size > 0) {
    modifiedProjectIds.forEach((pid) => markProjectDirty(pid));
  }
  markProjectDirty(COMM_PROJECT_ID);

  const poolDeleteCmd: Command = {
    id: `delete-pool-contact-${Date.now()}`,
    name: `Kontakt „${poolContactName}“ entfernt`,
    timestamp: Date.now(),
    execute: async () => {},
    undo: async () => {
      const now = Date.now();
      const stampedRestoredTasks = removedTasksList.map((t) => ({ ...t, updatedAt: now }));
      syncTracker.clearRecentlyDeleted([taskIdStr, ...removedTaskIds]);
      syncTracker.recordDirtyTasks([...removedTaskIds, ...unassignedTaskIds]);
      syncTracker.syncMultipleTasks(stampedRestoredTasks, true);

      const nextProjects = syncTracker.getAllProjects().map((proj) => {
        let changed = false;
        let projTasks = [...(proj.tasks || [])];
        let projMetadata = { ...(proj.metadata || {}) };

        const toRestore = stampedRestoredTasks.filter((t) =>
          areProjectIdsMatching(t.projectId || targetPId, proj.id)
        );
        if (toRestore.length > 0) {
          const existingIds = new Set(projTasks.map((pt) => String(pt.id)));
          projTasks = [
            ...projTasks,
            ...toRestore.filter((rt) => !existingIds.has(String(rt.id))),
          ];
          changed = true;
        }

        projTasks = projTasks.map((t) => {
          const backup = unassignedTasksBackup.find(
            (b) =>
              areProjectIdsMatching(b.projectId, proj.id) && String(b.taskId) === String(t.id)
          );
          if (backup) {
            changed = true;
            return {
              ...t,
              assignee: backup.previousAssignee,
              ...(backup.previousContactId !== undefined
                ? { contactId: backup.previousContactId }
                : {}),
              ...(backup.previousPersonKey !== undefined
                ? { personKey: backup.previousPersonKey }
                : {}),
              updatedAt: now,
            };
          }
          return t;
        });

        const stakeholdersToRestore = removedStakeholdersBackup.filter((b) =>
          areProjectIdsMatching(b.projectId, proj.id)
        );
        if (stakeholdersToRestore.length > 0) {
          projMetadata = {
            ...projMetadata,
            stakeholders: [
              ...(projMetadata.stakeholders || []),
              ...stakeholdersToRestore.map((b) => b.stakeholder),
            ],
          };
          changed = true;
        }

        if (changed) {
          return { ...proj, tasks: projTasks, metadata: projMetadata, lastModified: now };
        }
        return proj;
      });

      syncTracker.commitProjects(nextProjects, setAllProjects, modifiedProjectIds);

      for (const t of removedTasksList) {
        try {
          const docRef = doc(db, 'tasks', String(t.id));
          await setDoc(docRef, t, { merge: true });
        } catch (e) {
          console.warn('[Undo] Failed to restore contact task to Firestore:', e);
        }
      }

      for (const b of unassignedTasksBackup) {
        try {
          const docRef = doc(db, 'tasks', String(b.taskId));
          await updateDoc(docRef, {
            assignee: b.previousAssignee,
            ...(b.previousContactId !== undefined ? { contactId: b.previousContactId } : {}),
            ...(b.previousPersonKey !== undefined ? { personKey: b.previousPersonKey } : {}),
            updatedAt: Date.now(),
          });
        } catch (e) {
          console.warn('[Undo] Failed to restore unassigned task to Firestore:', e);
        }
      }

      if (modifiedProjectIds.size > 0) {
        modifiedProjectIds.forEach((pid) => markProjectDirty(pid));
      }
      markProjectDirty(COMM_PROJECT_ID);

      window.dispatchEvent(
        new CustomEvent('gwantt-task-restored', {
          detail: { name: poolContactName, assignee: poolContactName },
        })
      );
      addLogEntry('create', `Kontakt "${poolContactName}" wiederhergestellt`);
    },
    redo: async () => {
      onRedoDelete(taskIdStr);
    },
  };

  globalCommandManager.registerExecuted(poolDeleteCmd);
  undoToastManager.show({
    message: `Kontakt „${poolContactName}“ entfernt`,
    actionLabel: 'Rückgängig',
    icon: 'delete',
    onUndo: async () => {
      await globalCommandManager.undo();
    },
  });

  addLogEntry('delete', `Kontakt "${poolContactName}" aus Projekt entfernt`);
  return true;
}
